import { RunnerConfigurationKeys } from "@/components/runners/details/runner-configuration-keys";
import type { EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import type { PlainMessage } from "@bufbuild/protobuf";
import { RunnerKind, type EnvironmentClass } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";

export function environmentClassId(runnerId: string, name: string): string {
    return `environment-class-id-${runnerId}-${name}`;
}

/**
 * Returns the most suitable local environment class for a project using the local runner.
 * This is useful when deciding which class to use for authentication assertions on the frontend,
 * while the main selection logic happens on the backend.
 *
 * Mimics the behavior of `if project.LocalRunner` in `backend/api/environment.go`
 */
export const getPreferredLocalEnvironmentClass = (
    environmentClassEntries: EnvironmentClassEntry[],
): EnvironmentClassEntry | undefined => {
    const localClasses = environmentClassEntries
        .filter((classEntry) => classEntry.runner.kind === RunnerKind.LOCAL)
        // sort by status (active first, degraded second, rest third)
        .sort((a, b) => {
            if (a.runner.status?.phase === b.runner.status?.phase) {
                return 0;
            }
            return getStatusSortValue(a.runner.status?.phase) - getStatusSortValue(b.runner.status?.phase);
        });

    return localClasses[0];
};

const getStatusSortValue = (phase?: RunnerPhase) => {
    if (phase === RunnerPhase.ACTIVE) {
        return 0;
    }
    if (phase === RunnerPhase.DEGRADED) {
        return 1;
    }

    return 2;
};

export function defaultEnvironmentClasses(runnerId: string): PlainMessage<EnvironmentClass>[] {
    return [
        {
            displayName: "Regular",
            id: environmentClassId(runnerId, "regular"),
            runnerId: runnerId,
            description: "4 cores · 16GB RAM · 100GB Disk",
            enabled: true,
            configuration: [
                {
                    key: RunnerConfigurationKeys.AWSInstanceType,
                    value: "m6i.large",
                },
                {
                    key: RunnerConfigurationKeys.DiskSizeGB,
                    value: "100",
                },
                {
                    key: RunnerConfigurationKeys.SpotInstanceEnabled,
                    value: "false",
                },
            ],
        },
        {
            displayName: "Large",
            id: environmentClassId(runnerId, "large"),
            runnerId: runnerId,
            description: "8 cores · 32GB RAM · 256GB Disk",
            enabled: true,
            configuration: [
                {
                    key: RunnerConfigurationKeys.AWSInstanceType,
                    value: "m6i.2xlarge",
                },
                {
                    key: RunnerConfigurationKeys.DiskSizeGB,
                    value: "256",
                },
                {
                    key: RunnerConfigurationKeys.SpotInstanceEnabled,
                    value: "false",
                },
            ],
        },
        {
            displayName: "XLarge",
            id: environmentClassId(runnerId, "xlarge"),
            runnerId: runnerId,
            description: "32 cores · 128GB RAM · 300GB Disk",
            enabled: true,
            configuration: [
                {
                    key: RunnerConfigurationKeys.AWSInstanceType,
                    value: "m6i.8xlarge",
                },
                {
                    key: RunnerConfigurationKeys.DiskSizeGB,
                    value: "300",
                },
                {
                    key: RunnerConfigurationKeys.SpotInstanceEnabled,
                    value: "false",
                },
            ],
        },
        {
            displayName: "XXLarge",
            id: environmentClassId(runnerId, "xxlarge"),
            runnerId: runnerId,
            description: "48 cores · 192GB RAM · 300GB Disk",
            enabled: false,
            configuration: [
                {
                    key: RunnerConfigurationKeys.AWSInstanceType,
                    value: "m6i.12xlarge",
                },
                {
                    key: RunnerConfigurationKeys.DiskSizeGB,
                    value: "300",
                },
                {
                    key: RunnerConfigurationKeys.SpotInstanceEnabled,
                    value: "false",
                },
            ],
        },
    ];
}
